import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CContainedButton, COutlinedButton, CModal } from 'components/atoms';
import CCheckbox from 'components/atoms/checkbox/checkbox';
import Logo from '../../../../public/logo.png';

export default function CookieConsent({ onAccept, onReject }) {
	const [showModal, setShowModal] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [settings, setSettings] = useState({
		necessary: true,
		preferences: false,
		analytics: false,
		marketing: false,
	});
	const [expandedSections, setExpandedSections] = useState({
		necessary: false,
		analytics: false,
	});
	const { t } = useTranslation();

	const toggleSection = (section) => {
		setExpandedSections(prev => ({
			...prev,
			[section]: !prev[section]
		}));
	};

	useEffect(() => {
		const timer = setTimeout(() => setIsVisible(true), 100);
		return () => clearTimeout(timer);
	}, []);

	const handleToggle = (key) => {
		if (key === 'necessary') return; 
		setSettings((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const handleAcceptAll = () => {
		onAccept({ necessary: true, analytics: true });
		setIsVisible(false);
	};

	const handleReject = () => {
		onReject({ necessary: true, analytics: false });
		setIsVisible(false);
	};

	const handleAcceptSelected = () => {
		onAccept(settings);
		setShowModal(false);
	};

	const handleCancelSettings = () => {
		setShowModal(false);
	};
	
	const handleOpenSettings = () => {
		setShowModal(true);
	};

	const titleWithLogo = (
		<div className="flex items-center gap-2 p-4 border-b">
			<img src={Logo} alt="Koç Healthcare" className="h-6" />
			<span className="text-primary-800">{t("translation:cookie-consent.manage_cookie_preferences")}</span>
		</div>
	);

	return (
		<>
				<CModal
				open={showModal}
				onClose={() => setShowModal(false)}
				className="max-w-lg w-full max-h-[80vh] overflow-auto"
			>
				<div>
					{titleWithLogo}
					<div className="px-4 pb-4">
						<div className="mb-5 mt-5 w-full border rounded-lg overflow-hidden">
							<div className="flex items-center justify-between bg-gray-50 p-3">
								<div 
									className="flex items-center cursor-pointer mr-2"
									onClick={() => toggleSection('necessary')}
								>
									<svg 
										className={`w-5 h-5 transition-transform ${expandedSections.necessary ? 'transform rotate-90' : ''}`} 
										fill="none" 
										stroke="currentColor" 
										viewBox="0 0 24 24" 
										xmlns="http://www.w3.org/2000/svg"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
									</svg>
								</div>
								<div 
									className="flex-grow cursor-pointer"
									onClick={() => toggleSection('necessary')}
								>
									<span className="font-medium text-primary-800">{t("translation:cookie-consent.label_necessary")}</span>
								</div>
								<div>
									<CCheckbox
										id="necessary"
										checked={settings.necessary}
										onChange={() => {}}
										disabled={true}
									/>
								</div>
							</div>

							{expandedSections.necessary && (
								<div className="p-3 border-t">
									<p className="text-neutral-600 text-sm">{t("translation:cookie-consent.description_necessary_cookies")}</p>
								</div>
							)}
						</div>

						<div className="mb-4 w-full border rounded-lg overflow-hidden">
							<div className="flex items-center justify-between bg-gray-50 p-3">
								<div 
									className="flex items-center cursor-pointer mr-2"
									onClick={() => toggleSection('analytics')}
								>
									<svg 
										className={`w-5 h-5 transition-transform ${expandedSections.analytics ? 'transform rotate-90' : ''}`} 
										fill="none" 
										stroke="currentColor" 
										viewBox="0 0 24 24" 
										xmlns="http://www.w3.org/2000/svg"
									>
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
									</svg>
								</div>
								<div 
									className="flex-grow cursor-pointer"
									onClick={() => toggleSection('analytics')}
								>
									<span className="font-medium text-primary-800">{t("translation:cookie-consent.label_analytics")}</span>
								</div>
								<div>
									<CCheckbox
										id="analytics"
										checked={settings.analytics}
										onChange={() => handleToggle('analytics')}
									/>
								</div>
							</div>

							{expandedSections.analytics && (
								<div className="p-3 border-t">
									<p className="text-neutral-600 text-sm">{t("translation:cookie-consent.description_analytics_cookies")}</p>
								</div>
							)}
						</div>
					</div>
					<div className="flex justify-end gap-3 p-4 border-t">
						<COutlinedButton
							onClick={handleCancelSettings}
							className="px-6"
						>
							{t("translation:cookie-consent.cancel")}
						</COutlinedButton>
						<CContainedButton
							onClick={handleAcceptSelected}
							className="px-6"
						>
							{t("translation:cookie-consent.save")}
						</CContainedButton>
					</div>
				</div>
			</CModal>

			<div 
				className={`fixed left-0 bottom-0 w-full bg-white shadow-lg border-t border-neutral-200 transition-transform duration-500 ease-in-out z-50 ${
					isVisible ? 'translate-y-0' : 'translate-y-full'
				}`}
			>
				<div className="container mx-auto p-4">
					<div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
						<div className="flex-1">
							<h3 className="text-lg font-semibold mb-1 text-primary-800">{t("translation:cookie-consent.label_personal_data_protection")}</h3>
							<p className="text-neutral-600 text-sm">
							{t("translation:cookie-consent.description_personal_data_protection")}
							</p>
						</div>

						<div className="flex flex-col sm:flex-row gap-3 shrink-0">
							<COutlinedButton
								onClick={handleReject}
								className="sm:order-1"
							>
								{t("translation:cookie-consent.deny_all")}
							</COutlinedButton>
							<COutlinedButton
								onClick={handleOpenSettings}
								className="sm:order-2"
							>
								{t("translation:cookie-consent.customize")}
							</COutlinedButton>
							<CContainedButton
								onClick={handleAcceptAll}
								className="sm:order-3"
							>
								{t("translation:cookie-consent.accept_all")}
							</CContainedButton>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

